import { useCallback, useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { useLazyGetSectionsQuery } from "@/redux/service";
import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import BaseAccordion from "@/components_v2/Accordion/BaseAccordion";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import SurveyViewFormContentList from "../SurveyViewFormContentList";
import { SurveyViewFormContentProps } from "../../types";
import { SectionResponse } from "@/redux/survey/types";
import SelectionToolbar from "@/features/survey-views/form/components/SelectionToolbar";
import { useSurveyViewSelectableAnswers } from "@/features/survey-views/form/hooks/useSurveyViewSelectableAnswers";

import "./styles.css";

const SurveyViewFormContent = ({ form }: SurveyViewFormContentProps) => {
  const { t } = useTranslation();

  const [getSections, sectionsResponse] = useLazyGetSectionsQuery();

  const surveyId = useWatch({
    control: form.control,
    name: "survey",
  });
  const currentSelectedAnswers = useWatch({
    control: form.control,
    name: "answers",
  });
  const dataFormat = useWatch({
    control: form.control,
    name: "config.dataFormat",
  });

  const selectable = useSurveyViewSelectableAnswers({ surveyId, dataFormat });

  useEffect(() => {
    if (surveyId) {
      getSections({ survey: surveyId, pagination: "off", parentOnly: true });
    }
  }, [surveyId]);

  const handleSectionSelect = useCallback(
    (sectionQuestionIds: number[], checked: boolean) => {
      const questionIds = new Set(sectionQuestionIds);
      const sectionAnswers = selectable.selectableAnswers.filter((answer) =>
        questionIds.has(answer.question),
      );
      if (!sectionAnswers.length) return;

      if (checked) {
        const currentAnswerIds = new Set(
          currentSelectedAnswers.map((answer) => answer.answer),
        );

        const newlySelectedAnswers = sectionAnswers
          .filter((answer) => !currentAnswerIds.has(answer.id)) // Only add truly new answers
          .map((answer) => ({
            answer: answer.id,
            question: answer.question,
            attachedBlocks: answer.attachedBlocks.map((block) => ({
              id: block.id,
              sharingRights: block.sharingRights,
            })),
          }));

        if (newlySelectedAnswers.length > 0) {
          form.setValue("answers", [
            ...currentSelectedAnswers,
            ...newlySelectedAnswers,
          ]);
        }
      } else {
        const sectionAnswerIds = new Set(
          sectionAnswers.map((answer) => answer.id),
        );
        const filteredAnswers = currentSelectedAnswers.filter(
          (answer) => !sectionAnswerIds.has(answer.answer),
        );
        form.setValue("answers", filteredAnswers);
      }
    },
    [selectable.selectableAnswers, currentSelectedAnswers, form],
  );

  const sectionItems = useMemo(() => {
    if (!sectionsResponse.isSuccess) return [];

    const sectionsData = sectionsResponse.data as SectionResponse[];

    return sectionsData
      .map((parentSection) => {
        // Questions of the section tree that the chosen data format includes.
        const sectionQuestionIds = [parentSection, ...parentSection.tabs]
          .reduce<number[]>((acc, section) => {
            acc = acc.concat(section.questions);
            return acc;
          }, [])
          .filter(
            (id) =>
              !selectable.includedQuestionIds ||
              selectable.includedQuestionIds.has(id),
          );

        const answeredQuestionsCount = sectionQuestionIds.filter((id) =>
          selectable.answeredQuestionIds.has(id),
        ).length;

        const selectedQuestions = currentSelectedAnswers.reduce<number[]>(
          (acc, answer) => {
            if (
              sectionQuestionIds.includes(answer.question) &&
              !acc.includes(answer.question)
            ) {
              acc.push(answer.question);
            }
            return acc;
          },
          [],
        );

        const isSectionSelected =
          answeredQuestionsCount !== 0 &&
          selectedQuestions.length === answeredQuestionsCount;

        return {
          key: `section-${String(parentSection.id)}`,
          label: (
            <BaseCheckbox
              label={`${parentSection.title} ${selectedQuestions.length}/${answeredQuestionsCount}`}
              checked={isSectionSelected}
              disabled={answeredQuestionsCount === 0}
              onChange={(checked) =>
                handleSectionSelect(sectionQuestionIds, checked)
              }
            />
          ),
          children: (
            <SurveyViewFormContentList
              form={form}
              sectionIds={[
                parentSection.id,
                ...parentSection.tabs.map((section) => section.id),
              ]}
            />
          ),
        };
      })
      .filter((item) => item != null);
  }, [
    sectionsResponse,
    handleSectionSelect,
    currentSelectedAnswers,
    selectable.includedQuestionIds,
    selectable.answeredQuestionIds,
  ]);

  if (selectable.isError) {
    return <Navigate to="/" />;
  }

  return (
    <div className="flex flex-col gap-400 full-height">
      <h2 className="header-s-medium">
        {t("surveyViews.labels.formContentLabel")}
      </h2>
      <h3 className="body-m-regular">
        {t("surveyViews.labels.formContentDescription")}
      </h3>
      <SelectionToolbar form={form} selectable={selectable} />

      {(sectionsResponse.isLoading || selectable.isLoading) && <BaseSpinner />}
      {sectionsResponse.isError && <div>Error loading sections</div>}
      <BaseAccordion
        items={sectionItems}
        expandIconPosition="end"
        className="survey-view-accordion flex flex-col gap-200"
      />
    </div>
  );
};

export default SurveyViewFormContent;
